/**
 * Hooks para configuración de tarifas de envío (tienda física).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { useAuth } from '@/hooks/useAuth';
import type {
    StoreShippingPackageRateRow,
    StoreShippingPackageTypeGroup,
    StoreShippingRateRow,
} from '../types';

const QK_RATES = ['admin', 'store-shipping-rates'] as const;
const QK_PACKAGE = ['admin', 'store-shipping-package-rates'] as const;

export function useStoreShippingRatesList() {
    const { user } = useAuth();
    return useQuery<StoreShippingRateRow[]>({
        queryKey: QK_RATES,
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<StoreShippingRateRow[]>(
                'admin_list_store_shipping_rates',
                { p_admin_user_id: user!.id }
            );
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as StoreShippingRateRow[];
        },
        staleTime: 30_000,
    });
}

export interface UpdateShippingRateInput {
    rateId: string;
    priceCents: number;
    freeThresholdCents: number | null;
    clearFreeThreshold: boolean;
    deliveryDaysMin: number;
    deliveryDaysMax: number;
}

export function useUpdateStoreShippingRate() {
    const qc = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async (input: UpdateShippingRateInput) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc<Record<string, unknown>>(
                'admin_update_store_shipping_rate',
                {
                    p_admin_user_id: user.id,
                    p_rate_id: input.rateId,
                    p_price_cents: input.priceCents,
                    p_free_threshold_cents: input.freeThresholdCents,
                    p_clear_free_threshold: input.clearFreeThreshold,
                    p_delivery_days_min: input.deliveryDaysMin,
                    p_delivery_days_max: input.deliveryDaysMax,
                }
            );
            if (error) throw new Error(error.message);
            const norm = normalizeRpcResponse(data);
            return norm[0] ?? null;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK_RATES });
        },
    });
}

function toNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = typeof value === 'number' ? value : Number.parseFloat(String(value));
    return Number.isFinite(n) ? n : null;
}

export function groupPackageRates(rows: StoreShippingPackageRateRow[]): StoreShippingPackageTypeGroup[] {
    const byCode = new Map<string, StoreShippingPackageTypeGroup>();

    for (const row of rows) {
        let group = byCode.get(row.package_type_code);
        if (!group) {
            group = {
                code: row.package_type_code,
                label: row.package_type_label,
                dimensions: row.dimensions,
                billableWeightKg: toNumber(row.billable_weight_kg),
                billableWeightNote: row.billable_weight_note,
                sortOrder: toNumber(row.package_sort_order) ?? 0,
                pricesByZone: {},
            };
            byCode.set(row.package_type_code, group);
        }
        group.pricesByZone[row.zone_code] = {
            id: row.id,
            priceCents: toNumber(row.price_cents) ?? 0,
            updatedAt: row.updated_at,
        };
    }

    return [...byCode.values()].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function useStoreShippingPackageRatesList() {
    const { user } = useAuth();
    return useQuery<StoreShippingPackageRateRow[]>({
        queryKey: QK_PACKAGE,
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<StoreShippingPackageRateRow[]>(
                'admin_list_store_shipping_package_rates',
                { p_admin_user_id: user!.id }
            );
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as StoreShippingPackageRateRow[];
        },
        staleTime: 30_000,
    });
}

export interface UpdatePackagePriceInput {
    id: string;
    priceCents: number;
}

export function useUpdateStoreShippingPackagePrices() {
    const qc = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async (updates: UpdatePackagePriceInput[]) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc<Record<string, unknown>>(
                'admin_update_store_shipping_package_prices',
                {
                    p_admin_user_id: user.id,
                    p_updates: updates.map((u) => ({ id: u.id, priceCents: u.priceCents })),
                }
            );
            if (error) throw new Error(error.message);
            const norm = normalizeRpcResponse(data);
            return norm[0] ?? null;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK_PACKAGE });
        },
    });
}

export interface UpsertPackagePriceInput {
    packageTypeCode: string;
    zoneCode: string;
    priceCents: number;
}

export function useUpsertStoreShippingPackagePrices() {
    const qc = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async (upserts: UpsertPackagePriceInput[]) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc<Record<string, unknown>>(
                'admin_upsert_store_shipping_package_prices',
                {
                    p_admin_user_id: user.id,
                    p_upserts: upserts.map((u) => ({
                        packageTypeCode: u.packageTypeCode,
                        zoneCode: u.zoneCode,
                        priceCents: u.priceCents,
                    })),
                }
            );
            if (error) throw new Error(error.message);
            const norm = normalizeRpcResponse(data);
            return norm[0] ?? null;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK_PACKAGE });
        },
    });
}
